'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface ServiceRequest {
  id?: string;
  userProfileId: string;
  serviceType: string;
  pickupAddress: string;
  deliveryAddress: string;
  pickupDate: string;
  pickupTime?: string;
  itemDescription: string;
  itemSize?: string;
  requiresAssembly?: boolean;
  requiresPacking?: boolean;
  specialInstructions?: string;
  estimatedPrice?: number;
  finalPrice?: number;
  status?: string;
  priority?: string;
  assignedDriverId?: string;
  assignedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const userServiceRequestService = {
  async createRequest(request: ServiceRequest) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_service_requests')
        .insert({
          user_profile_id: request.userProfileId,
          service_type: request.serviceType,
          pickup_address: request.pickupAddress,
          delivery_address: request.deliveryAddress,
          pickup_date: request.pickupDate,
          pickup_time: request.pickupTime,
          item_description: request.itemDescription,
          item_size: request.itemSize,
          requires_assembly: request.requiresAssembly,
          requires_packing: request.requiresPacking,
          special_instructions: request.specialInstructions,
          estimated_price: request.estimatedPrice,
          status: request.status || 'pending',
          priority: request.priority || 'normal',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        serviceType: data.service_type,
        pickupAddress: data.pickup_address,
        deliveryAddress: data.delivery_address,
        pickupDate: data.pickup_date,
        pickupTime: data.pickup_time,
        itemDescription: data.item_description,
        itemSize: data.item_size,
        requiresAssembly: data.requires_assembly,
        requiresPacking: data.requires_packing,
        specialInstructions: data.special_instructions,
        estimatedPrice: data.estimated_price,
        finalPrice: data.final_price,
        status: data.status,
        priority: data.priority,
        assignedDriverId: data.assigned_driver_id,
        assignedAt: data.assigned_at,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async getRequestsByProfileId(userProfileId: string): Promise<ServiceRequest[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_service_requests')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((req: any) => ({
        id: req.id,
        userProfileId: req.user_profile_id,
        serviceType: req.service_type,
        pickupAddress: req.pickup_address,
        deliveryAddress: req.delivery_address,
        pickupDate: req.pickup_date,
        pickupTime: req.pickup_time,
        itemDescription: req.item_description,
        itemSize: req.item_size,
        requiresAssembly: req.requires_assembly,
        requiresPacking: req.requires_packing,
        specialInstructions: req.special_instructions,
        estimatedPrice: req.estimated_price,
        finalPrice: req.final_price,
        status: req.status,
        priority: req.priority,
        assignedDriverId: req.assigned_driver_id,
        assignedAt: req.assigned_at,
        createdAt: req.created_at,
        updatedAt: req.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async getActiveRequests(userProfileId: string): Promise<ServiceRequest[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_service_requests')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .in('status', ['pending', 'confirmed', 'in_progress'])
        .order('pickup_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((req: any) => ({
        id: req.id,
        userProfileId: req.user_profile_id,
        serviceType: req.service_type,
        pickupAddress: req.pickup_address,
        deliveryAddress: req.delivery_address,
        pickupDate: req.pickup_date,
        pickupTime: req.pickup_time,
        itemDescription: req.item_description,
        itemSize: req.item_size,
        requiresAssembly: req.requires_assembly,
        requiresPacking: req.requires_packing,
        specialInstructions: req.special_instructions,
        estimatedPrice: req.estimated_price,
        finalPrice: req.final_price,
        status: req.status,
        priority: req.priority,
        assignedDriverId: req.assigned_driver_id,
        assignedAt: req.assigned_at,
        createdAt: req.created_at,
        updatedAt: req.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async updateRequest(requestId: string, updates: Partial<ServiceRequest>) {
    const supabase = createClient();

    try {
      const updateData: any = {};

      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.specialInstructions !== undefined) updateData.special_instructions = updates.specialInstructions;

      const { data, error } = await supabase
        .from('user_service_requests')
        .update(updateData)
        .eq('id', requestId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },
};